import React from "react";

// A filter answers "what should I work on next?", so the labels name the action rather
// than the status. `in_progress` is the bucket of questions attempted but not yet
// mastered — "Needs review" says why a student would pick it, and matches the wording
// the practice quiz results page already uses for the same idea.
const DEFAULT_FILTERS = [
  ["all", "All"],
  ["not_started", "Not started"],
  ["in_progress", "Needs review"],
  ["skipped", "Skipped"],
  ["mastered", "Mastered"],
];

const labelFor = (filters, key) =>
  filters.find(([k]) => k === key)?.[1] || "All";

const buttonText = (filters, filter, statusCounts) => {
  const label = labelFor(filters, filter);
  const count =
    filter === "all"
      ? statusCounts?.all ?? 0
      : statusCounts?.[filter] ?? 0;

  // keep compact + “legit”
  return `Filter: ${label}`;
};

const QuestionFilter = ({
  filter = "all",
  statusCounts = {},
  availableFilterKeys = [],
  shouldShow = false,
  filters = DEFAULT_FILTERS,

  // actions
  onApplyFilter,

  // ui state
  open,
  setOpen,

  // layout
  align = "right", // "right" | "left"
  widthClass = "w-56",
  buttonClassName = "",
}) => {
  if (!shouldShow) return null;

  const menuAlignClass = align === "left" ? "left-0" : "right-0";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          buttonClassName ||
          "inline-flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-gray-200"
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{buttonText(filters, filter, statusCounts)}</span>
        <span className="text-gray-400">
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>

      {open && (
        <>
          {/* click-away */}
          <button
            type="button"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          <div
            className={`absolute ${menuAlignClass} z-20 mt-2 ${widthClass} overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg`}
            role="listbox"
          >
            {filters
              .filter(([key]) => availableFilterKeys.includes(key))
              .map(([key, label]) => {
                const isActive = key === filter;
                const count =
                  key === "all"
                    ? statusCounts?.all ?? 0
                    : statusCounts?.[key] ?? 0;

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      if (onApplyFilter) onApplyFilter(key);
                      setOpen(false);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm transition flex items-center justify-between ${
                      isActive
                        ? "bg-gray-50 text-gray-900"
                        : "text-gray-800 hover:bg-gray-50"
                    }`}
                    role="option"
                    aria-selected={isActive}
                  >
                    <span className="font-medium">{label}</span>
                    <span className="text-xs font-semibold text-gray-400">
                      {count}
                    </span>
                  </button>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
};

export default QuestionFilter;
